import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

function getApiBaseUrl() {
  const fromWindow =
    typeof window !== "undefined" ? window.PARANOIDI_API_BASE_URL : "";

  if (fromWindow) {
    return String(fromWindow).replace(/\/+$/, "");
  }

  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
  ) {
    return "http://localhost:4000/api";
  }

  return "/backend/api";
}

export default function ResetPasswordPage() {
  const { t } = useTranslation("admin");

  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return String(params.get("token") || "").trim();
  }, []);

  const [password, setPassword] = useState("");
  const [passwordAgain, setPasswordAgain] = useState("");
  const [message, setMessage] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordAgain, setShowPasswordAgain] = useState(false);

  const hasPassword = password.length > 0;
  const hasPasswordAgain = passwordAgain.length > 0;
  const isLongEnough = password.length >= 8;
  const passwordsMatch =
    hasPassword && hasPasswordAgain && password === passwordAgain;

  async function handleSubmit(event) {
    event.preventDefault();

    setMessage("");
    setIsSuccess(false);

    if (!token) {
      setMessage(t("resetPasswordPage.validation.invalidToken"));
      return;
    }

    if (!isLongEnough) {
      setMessage(t("resetPasswordPage.validation.passwordTooShort"));
      return;
    }

    if (password !== passwordAgain) {
      setMessage(t("resetPasswordPage.validation.passwordsMismatch"));
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${getApiBaseUrl()}/auth/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify({
          token,
          password,
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok || !data?.ok) {
        throw new Error(t("resetPasswordPage.errors.resetFailed"));
      }

      setIsSuccess(true);
      setPassword("");
      setPasswordAgain("");
      setMessage(t("resetPasswordPage.success.message"));
    } catch (error) {
      setMessage(t("resetPasswordPage.errors.resetFailed"));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <main className="min-h-screen bg-black px-4 py-10 text-white sm:px-6">
      <div className="mx-auto flex min-h-[80vh] max-w-xl items-center justify-center">
        <section className="w-full rounded-[2rem] border border-red-400/20 bg-zinc-950/90 p-6 text-center shadow-2xl shadow-red-950/30 sm:p-8">
          <p className="text-xs font-black uppercase tracking-[0.22em] text-red-300">
            Paranoidi
          </p>

          <h1 className="mt-4 text-3xl font-black uppercase tracking-[-0.05em] text-white sm:text-5xl">
            {isSuccess
              ? t("resetPasswordPage.success.title")
              : t("resetPasswordPage.title")}
          </h1>

          <p className="mx-auto mt-4 max-w-md text-sm font-semibold leading-7 text-zinc-400 sm:text-base">
            {isSuccess
              ? t("resetPasswordPage.success.description")
              : t("resetPasswordPage.description")}
          </p>

          {isSuccess ? (
            <div className="mt-7 grid gap-4">
              {message && (
                <p className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-4 py-3 text-center text-sm font-bold leading-6 text-emerald-100">
                  {message}
                </p>
              )}

              <a
                href="/admin"
                className="inline-flex items-center justify-center rounded-2xl bg-red-500 px-5 py-4 text-sm font-black uppercase tracking-[0.16em] text-white no-underline transition duration-200 hover:-translate-y-0.5 hover:bg-red-400 active:scale-[0.98]"
              >
                {t("resetPasswordPage.actions.signIn")}
              </a>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-7 grid gap-3 text-left">
              <label className="grid gap-2 text-sm font-black text-zinc-200">
                {t("resetPasswordPage.fields.newPassword")}

                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoComplete="new-password"
                    className="w-full rounded-2xl border border-white/10 bg-white px-4 py-3 pr-24 text-sm font-black text-black outline-none transition focus:border-red-400"
                    placeholder={t(
                      "resetPasswordPage.fields.minimumLengthPlaceholder"
                    )}
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    className="absolute right-3 top-1/2 flex h-8 min-w-[4.5rem] items-center justify-center rounded-xl px-3 text-xs font-black uppercase tracking-[0.08em] text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-black"
                    style={{ transform: "translateY(-50%)" }}
                  >
                    {showPassword
                      ? t("resetPasswordPage.actions.hide")
                      : t("resetPasswordPage.actions.show")}
                  </button>
                </div>
              </label>

              <label className="grid gap-2 text-sm font-black text-zinc-200">
                {t("resetPasswordPage.fields.repeatPassword")}

                <div className="relative">
                  <input
                    type={showPasswordAgain ? "text" : "password"}
                    value={passwordAgain}
                    onChange={(event) => setPasswordAgain(event.target.value)}
                    autoComplete="new-password"
                    className="w-full rounded-2xl border border-white/10 bg-white px-4 py-3 pr-24 text-sm font-black text-black outline-none transition focus:border-red-400"
                    placeholder={t(
                      "resetPasswordPage.fields.repeatPasswordPlaceholder"
                    )}
                  />

                  <button
                    type="button"
                    onClick={() =>
                      setShowPasswordAgain((current) => !current)
                    }
                    className="absolute right-3 top-1/2 flex h-8 min-w-[4.5rem] items-center justify-center rounded-xl px-3 text-xs font-black uppercase tracking-[0.08em] text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-black"
                    style={{ transform: "translateY(-50%)" }}
                  >
                    {showPasswordAgain
                      ? t("resetPasswordPage.actions.hide")
                      : t("resetPasswordPage.actions.show")}
                  </button>
                </div>
              </label>

              <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm font-bold leading-6 text-zinc-300">
                <p
                  className={
                    isLongEnough ? "text-emerald-200" : "text-zinc-400"
                  }
                >
                  {isLongEnough ? "✓" : "•"}{" "}
                  {t("resetPasswordPage.checklist.minimumLength")}
                </p>

                <p
                  className={
                    passwordsMatch
                      ? "text-emerald-200"
                      : hasPasswordAgain
                        ? "text-red-200"
                        : "text-zinc-400"
                  }
                >
                  {passwordsMatch ? "✓" : "•"}{" "}
                  {t("resetPasswordPage.checklist.passwordsMatch")}
                </p>
              </div>

              {message && (
                <p className="rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-center text-sm font-bold leading-6 text-red-100">
                  {message}
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="mt-2 inline-flex items-center justify-center gap-3 rounded-2xl bg-red-500 px-5 py-4 text-sm font-black uppercase tracking-[0.16em] text-white transition duration-200 hover:-translate-y-0.5 hover:bg-red-400 active:scale-[0.98] disabled:cursor-not-allowed disabled:translate-y-0 disabled:opacity-60"
              >
                {isSubmitting && (
                  <span
                    aria-hidden="true"
                    className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
                  />
                )}

                <span>
                  {isSubmitting
                    ? t("resetPasswordPage.actions.saving")
                    : t("resetPasswordPage.actions.submit")}
                </span>
              </button>

              <p className="pt-2 text-center text-sm font-bold text-zinc-400">
                {t("resetPasswordPage.rememberedPassword")}{" "}
                <a
                  href="/admin"
                  className="text-red-300 underline-offset-4 transition hover:text-red-200 hover:underline"
                >
                  {t("resetPasswordPage.actions.signIn")}
                </a>
              </p>
            </form>
          )}
        </section>
      </div>
    </main>
  );
}